import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SearchService } from "../../../../services/search.service";
import { Subscription, combineLatest } from 'rxjs';
import { RoutingService } from 'src/app/services/routing.service';
import { TabId } from 'src/app/enums/tab-id.enum';
import { Entity } from 'src/app/models/mvp-query-response-dto';

import { SearchFieldsService } from 'src/app/services/search-fields.service';
import { ControlType } from 'src/app/components/arcgis-map/constants/control-type.enum';
import { MapControlItem } from 'src/app/components/arcgis-map/constants/map-control-item.interface';
import { ARCGIS_MAP_LIFECYCLE } from 'src/app/components/arcgis-map/constants/map-life-cycle.interface';
import { ResultsMapService } from 'src/app/services/results-map.service';
import { ArcGisMapComponent } from 'src/app/components/arcgis-map/arcgis-map.component';
import { LanguageService } from 'src/app/services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/services/auth.service';
import { Inject } from '@angular/core';
import { EntityType } from 'src/app/enums/entity-type.enum';

@Component({
  selector: 'app-results-body-map',
  templateUrl: './results-body-map.component.html',
  styleUrls: ['./results-body-map.component.scss'],
  providers: [
    {
      provide: ARCGIS_MAP_LIFECYCLE,
      useClass: ResultsMapService,
    },
  ],
})
export class ResultsBodyMapComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(ArcGisMapComponent) private arcGisMapRef?: ArcGisMapComponent;
  private entityDetailsSubscription?: Subscription;
  @Input() isLoading: boolean = false;
  @Input() isErrorTooManyRows: boolean = false;
  @Input() isErrorNoPolygons: boolean = false;
  @Input() selectedEntityTypes: string[] = [];
  @Output() handleMapClick = new EventEmitter<Entity[]>();
  @Output() entityTypesChanged = new EventEmitter<string[]>(); // Emit when map layer visibility changes
  @Input() entities: Entity[] = [];
  public height = "600";
  public width = "1300";
  selectedEntity: number = 0;
  shapes: any[] = [];
  private baseShapes: any[] = []; // Store unfiltered shapes from service
  needZoom: boolean = false;
  searchFields: any;
  searchShapes: any[] = [];
  selectedEntityId: number | null = null;
  currentLanguage: 'he' | 'en' = 'he';
  currentLanguageSubscription?: Subscription;
  mapControls: MapControlItem[] = [];
  private internalUser: boolean = false;
  private internalUserSubscription?: Subscription;
  private mapView: any = null;
  autoExpandSearch: boolean = false;
  private queryParamsSubscription?: Subscription;

  constructor(
    public searchService: SearchService,
    private routingService: RoutingService,
    private searchFieldsService: SearchFieldsService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    @Inject(ARCGIS_MAP_LIFECYCLE) private mainMapService: ResultsMapService
  ) {
  }  public ngOnInit(): void {
    // Subscribe to query parameters
    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      this.autoExpandSearch = params['autoExpandSearch'] === 'true';
    });

    this.searchFieldsService.searchFields$.subscribe((searchFields) => {
      this.searchFields = searchFields;

      // Rebuild searchShapes from scratch to reflect current regions
      this.searchShapes = [];

      if (this.searchFields.regions && this.searchFields.regions.length > 0) {
        this.searchFields.regions.forEach((region: any, index: number) => {
          const shape: any = {
            layerName: this.translate.instant('iaa.map.searchRegionsLayer'),
            wkt: region.id,
            shapeId: `shape_${index}`,
            shapeData: {},
            hidden: false,
            styledParams: {
              geometryType: "Polygon",
              fillColor: [0, 0, 0, 0.08],
              borderColor: [0, 0, 0, 0.7],
              borderWidth: 1.3,
            }
          }
          this.searchShapes.push(shape);
        });
      }
    });
    this.currentLanguageSubscription = this.languageService.currentLanguage$.subscribe((lang) => {
      this.currentLanguage = lang as 'he' | 'en';
      this.translate.get('iaa.map').subscribe(() => {
        this.populateMapControls();
      });
    });
    this.internalUserSubscription = this.authService.internalUser$.subscribe((isInternalUser) => {
      this.internalUser = isInternalUser;
      this.translate.get('iaa.map').subscribe(() => {
        this.populateMapControls();
      });
    });
    this.searchService.shapes$.subscribe((shapes: any) => {
      if (shapes.length > 0) {
        const queryResultsLayerName = this.translate.instant('iaa.map.queryResultsLayer');
        const searchRegionsShapes = this.searchShapes.filter((s: any) => s.layerName === this.translate.instant('iaa.map.searchRegionsLayer'));
        const queryResultsShapes = shapes.filter((s: any) => s.layerName === queryResultsLayerName);
        const otherShapes = shapes.filter((s: any) => s.layerName !== queryResultsLayerName);

        // Store the base shapes (unfiltered) from the service
        const newBaseShapes = [...searchRegionsShapes, ...queryResultsShapes, ...otherShapes];

        // Only update if the base shapes have actually changed (new search results)
        // Compare: 1) shape count, 2) WKT strings, 3) entity counts per shape
        const shapesChanged = this.baseShapes.length !== newBaseShapes.length ||
          JSON.stringify(this.baseShapes.map(s => s.wkt).sort()) !== JSON.stringify(newBaseShapes.map(s => s.wkt).sort()) ||
          JSON.stringify(this.baseShapes.map(s => s.shapeData?.entities?.length || 0)) !== JSON.stringify(newBaseShapes.map(s => s.shapeData?.entities?.length || 0));

        if (shapesChanged) {
          this.baseShapes = newBaseShapes;
          this.shapes = [...this.baseShapes];

          // Apply entity filtering to shapes
          if (this.entities.length > 0) {
            this.shapes = this.updateShapesToHidden(this.entities);
          }
        }
      }
    });
    this.entityDetailsSubscription = combineLatest([this.routingService.entityDetails$, this.routingService.activeTabIndex$])
      .subscribe(([entityDetails, tabId]) => {
        const { id } = entityDetails;

        if(tabId !== TabId.MAP) {
          const oldValue = this.selectedEntityId;
          this.selectedEntityId = null;
          // Force a different value first to ensure Angular detects the change
          if (oldValue === null) {
            this.selectedEntityId = -1;
            this.cdr.detectChanges();
          }
          this.selectedEntityId = null;
          this.cdr.detectChanges();
          this.syncOrthophotoLayers();
        }

        else if (!!id && tabId === TabId.MAP) {
          this.selectedEntity = id;

          // Clear selection first to ensure change detection triggers properly
          this.selectedEntityId = null;
          this.cdr.detectChanges();
          this.selectedEntityId = id;
          this.cdr.detectChanges();
          this.syncOrthophotoLayers();

        } else if (tabId === TabId.MAP && !id) {
          // Clear selection when navigating directly to map
          this.selectedEntity = 0;
          this.selectedEntityId = null;
          this.cdr.detectChanges();

          this.shapes = [...this.shapes];
          this.cdr.detectChanges();
          this.syncOrthophotoLayers();
        }
      });
    }
  public ngOnChanges(changes: any): void {
    if (changes['entities'] || changes['selectedEntityTypes']) {
      // When entities or entity type filter changes, update shape visibility
      if (this.baseShapes.length > 0) {
        this.shapes = this.updateShapesToHidden(this.entities);
        this.cdr.detectChanges();
      }
    }
    if (changes['entities']) {
      this.syncOrthophotoLayers();
    }
  }

  private syncOrthophotoLayers(): void {
    if (!this.arcGisMapRef) {
      return;
    }

    const selectedEntity = this.entities.find((entity) => entity.entityId === this.selectedEntityId);
    const shouldDisplaySingleOrthophoto =
      !!this.selectedEntityId &&
      (selectedEntity?.entityType === EntityType.ORTHOPHOTO ||
        selectedEntity?.entityType === 'orthophotos');

    this.arcGisMapRef.displayOrthophotoEntities(
      shouldDisplaySingleOrthophoto && selectedEntity ? [selectedEntity] : []
    );
  }
  updateShapesToHidden(entities: Entity[]): any[] {
    const visibleEntityIds = new Set(entities.map(e => e.entityId));
    const queryResultsLayerName = this.translate.instant('iaa.map.queryResultsLayer');

    const result = this.baseShapes.map((shape) => {
      // Skip non-queryResults layers
      if (shape.layerName !== queryResultsLayerName) {
        return shape;
      }

      // Get and filter entities for this shape
      const originalEntities = shape.shapeData?.entities || [shape.shapeData?.entity];

      const filteredEntities = originalEntities.filter((entity: Entity) =>
        visibleEntityIds.has(entity.entityId) &&
        (!this.selectedEntityTypes?.length || this.selectedEntityTypes.includes(entity.entityType))
      );

      // Hide shape if no entities match
      if (filteredEntities.length === 0) {
        return { ...shape, hidden: true, popupTemplate: null };
      }

      // Update shape with filtered entities
      const primaryEntity = filteredEntities[0];
      const popupTemplate = filteredEntities.length <= 10
        ? this.searchService.createShapesPopup(queryResultsLayerName, { entity: primaryEntity })
        : this.searchService.createRedundantShapesPopup({ entities: filteredEntities, entity: primaryEntity });

      return {
        ...shape,
        hidden: false,
        shapeData: { ...shape.shapeData, entity: primaryEntity, entities: filteredEntities },
        popupTemplate
      };
    });

    return result;
  }


  handleSelectedEntitiesFromMap(entities: Entity[]) {
    this.handleMapClick.emit(entities);
  }

  handleMapViewReady(view: any): void {
    this.mapView = view;
    this.syncOrthophotoLayers();
  }

  handleRightClick(): void {
  }

  private getQueryResultsLayer(): any {
    if (!this.mapView?.map) {
      return null;
    }

    const queryResultsLayerName = this.translate.instant('iaa.map.queryResultsLayer');
    return this.mapView.map.allLayers.find((layer: any) =>
      layer.title === queryResultsLayerName && layer.type === 'group'
    );
  }

  populateMapControls(): void {
    const position = this.currentLanguage === 'he' ? 'top-left' : 'top-right';
      this.mapControls = [
		{ position: position, type: ControlType.FullScreen },
		{ position: position, type: ControlType.Zoom },
		{
			position: position,
			type: ControlType.Locate,
			attributes: { scale: 100 },
		},
		{
			position: position,
			type: ControlType.Search,
			attributes: {
				includeDefaultSourcesDisabled: true
			},
			expand: {
				id: 'search-expand',
				position: position,
				expandIcon: 'search',
				expandTooltip: `${this.translate.instant('iaa.map.search-gush-parcel')}`,
				mode: 'floating',
			},
		},
    		{
			position: position,
			type: ControlType.BasemapGallery,
			expand: {
				id: 'basemap-gallery-expand',
				position: position,
				expandIcon: 'basemap',
				expandTooltip: `${this.translate.instant('iaa.map.expand-basemap-gallery')}`,
				mode: 'floating',
			},
		},
    {
			position: position,
			type: ControlType.LayerList,
			expand: {
				id: 'layer-list-expand',
				position: position,
				expandIcon: 'layers',
				expandTooltip: `${this.translate.instant('iaa.map.expand-layers')}`,
				mode: 'floating',
			},
		},
		{
			position: position,
			type: ControlType.Print,
			attributes: {
				allowedFormats: 'all',
				allowedLayouts: 'all',
				printServiceUrl:
					'https://gis.israntique.org.il/arcgis/rest/services/IAAPrintTemplates/IAAPrintTemplateGrid25000/GPServer/Export%20Web%20Map',
			},
			expand: {
				position: position,
				expandIcon: 'print',
				expandTooltip: `${this.translate.instant('iaa.map.expand-print')}`,
				mode: 'floating',
			},
		},
    {
			position: position,
			type: ControlType.AreaMeasurement2D,
			attributes: {
				unit: 'metric',
				locale: this.currentLanguage
			},
			expand: {
				position: position,
				expandIcon: 'polygon',
				expandTooltip: `${this.translate.instant('iaa.map.expand-area-measurement')}`,
				mode: 'floating',
			},
		},
    {
			position: position,
			type: ControlType.DistanceMeasurement2D,
			attributes: {
				unit: 'metric',
				locale: this.currentLanguage
			},
			expand: {
				position: position,
				expandIcon: 'measure-line',
				expandTooltip: `${this.translate.instant('iaa.map.expand-distance-measurement')}`,
				mode: 'floating',
			},
		},
    {
			position: this.currentLanguage === 'he' ? 'bottom-left' : 'bottom-right',
			type: ControlType.CoordinateConversion
		},
	];

  }



  ngOnDestroy(): void {
    // Clean up subscriptions
    this.entityDetailsSubscription?.unsubscribe();
    this.currentLanguageSubscription?.unsubscribe();
    this.internalUserSubscription?.unsubscribe();
    this.queryParamsSubscription?.unsubscribe();
  }
}

